import React from "react";
import {
  ListItemIcon,
  listItemIconClasses,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  typographyClasses,
} from "@mui/material";
import { PencilLine, Trash } from "@phosphor-icons/react";

export interface MarkerActionsMenuProps {
  /** Whether the menu is visible. */
  open: boolean;
  /**
   * Viewport position the menu opens at — where the marker line was clicked.
   * Required: the menu is positioned by this point (`anchorReference`
   * "anchorPosition"). Keep it while closing so the exit transition doesn't jump.
   */
  anchorPosition: { top: number; left: number };
  /** Called when the menu should close (backdrop click, Escape, item pick). */
  onClose: () => void;
  /** Called when "Edit marker" is picked. The item is hidden when omitted. */
  onEdit?: () => void;
  /** Called when "Delete marker" is picked. The item is hidden when omitted. */
  onDelete?: () => void;
}

/**
 * Context menu for a chart marker line, opened where the line was clicked.
 * Offers the actions the caller supports: edit and/or delete.
 *
 * @param props
 * @param props.open             - Whether the menu is visible
 * @param props.anchorPosition   - Viewport position the menu opens at
 * @param props.onClose          - Backdrop click / Escape / item pick handler
 * @param [props.onEdit]         - "Edit marker" handler; hides the item when omitted
 * @param [props.onDelete]       - "Delete marker" handler; hides the item when omitted
 */
const MarkerActionsMenu: React.FC<MarkerActionsMenuProps> = ({
  open,
  anchorPosition,
  onClose,
  onEdit,
  onDelete,
}) => {
  const handleItem = (callback: () => void) => () => {
    callback();
    onClose();
  };

  return (
    <Menu
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      MenuListProps={{ "aria-label": "Marker actions" }}
      slotProps={{ paper: { elevation: 3 } }}
    >
      {onEdit && (
        <MenuItem onClick={handleItem(onEdit)} aria-label="Edit marker">
          <ListItemIcon>
            <PencilLine size={20} />
          </ListItemIcon>
          <ListItemText>
            <Typography component="p" variant="body.small">
              Edit marker
            </Typography>
          </ListItemText>
        </MenuItem>
      )}

      {onDelete && (
        <MenuItem
          onClick={handleItem(onDelete)}
          aria-label="Delete marker"
          sx={{
            color: "error.main",
            // pkg-theme colors MenuItem icons/text through nested selectors, which
            // beat `color` on the children; match that specificity from the item.
            [`& .${listItemIconClasses.root}, & .${typographyClasses.root}`]: {
              color: "error.main",
            },
          }}
        >
          <ListItemIcon>
            <Trash size={20} />
          </ListItemIcon>
          <ListItemText>
            <Typography component="p" variant="body.small">
              Delete marker
            </Typography>
          </ListItemText>
        </MenuItem>
      )}
    </Menu>
  );
};

export default MarkerActionsMenu;
